import { useEffect, useRef, useState } from 'react';
import { useBrand } from '../BrandProvider.js';
import { brandLocalDate } from '../util/brand-date.js';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export interface MonthPickerProps {
  /** The selected month, "YYYY-MM". */
  value: string;
  /** Called with a guaranteed-valid "YYYY-MM" (built from the clicked month + shown year). */
  onChange: (value: string) => void;
  id?: string;
  /** Classes for the trigger button — pass the page's input styling so it matches the other fields. */
  className?: string;
}

/**
 * A click-only month + year picker (s53). The trigger shows "July 2026" and opens a calendar-style
 * popover: a year stepper plus a grid of the twelve months, with no day selector. The emitted value
 * is always "YYYY-MM" assembled from the clicked month and the shown year, so a malformed month can
 * never reach the API — this replaces the browser-dependent `<input type="month">`, which on some
 * setups fed a non-YYYY-MM value on change and tripped the "month must be YYYY-MM" error.
 */
export function MonthPicker({ value, onChange, id, className = '' }: MonthPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { brand } = useBrand();
  // A malformed value falls back to this month on the portal brand's clock (02 brand clock, 04 #136).
  const safe = /^\d{4}-\d{2}$/.test(value) ? value : brandLocalDate(brand?.timezone ?? null).slice(0, 7);
  const selYear = Number(safe.slice(0, 4));
  const selMonth = Number(safe.slice(5, 7)); // 1-12
  const [viewYear, setViewYear] = useState(selYear);

  // Show the selection's year each time the popover opens (or the selection's year changes).
  useEffect(() => {
    if (open) setViewYear(selYear);
  }, [open, selYear]);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (month1to12: number): void => {
    onChange(`${viewYear}-${String(month1to12).padStart(2, '0')}`);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        id={id}
        className={className}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {MONTHS_LONG[selMonth - 1]} {selYear}
      </button>
      {open ? (
        <div
          className="absolute left-0 z-30 mt-1 w-64 rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] p-3 shadow-lg"
          role="dialog"
          aria-label="Choose month"
        >
          <div className="flex items-center justify-between">
            <button
              type="button"
              className="rounded-md px-2 py-1 text-lg leading-none hover:bg-[rgb(var(--edge-rgb)/0.05)]"
              onClick={() => setViewYear((y) => y - 1)}
              aria-label="Previous year"
            >
              &lsaquo;
            </button>
            <span className="font-medium">{viewYear}</span>
            <button
              type="button"
              className="rounded-md px-2 py-1 text-lg leading-none hover:bg-[rgb(var(--edge-rgb)/0.05)]"
              onClick={() => setViewYear((y) => y + 1)}
              aria-label="Next year"
            >
              &rsaquo;
            </button>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-1">
            {MONTHS_SHORT.map((m, i) => {
              const isSel = viewYear === selYear && i + 1 === selMonth;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => pick(i + 1)}
                  className={`rounded-md px-2 py-1.5 text-sm ${
                    isSel ? 'bg-[var(--color-primary)] text-[var(--btn-primary-text)] ring-1 ring-inset ring-[var(--btn-primary-edge)]' : 'hover:bg-[rgb(var(--edge-rgb)/0.05)]'
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
